import type {
  EndCardOverlay,
  FreeTextOverlay,
  LabelOverlay,
  LogoOverlay,
  TelopFont,
  TitleCardOverlay,
} from '@shared/types'

/**
 * HTML/CSS approximation of the server-rendered overlay PNG, used while
 * POST /api/overlays/render is 501 (S2 not shipped yet) or errors.
 * Mirrors analysis/VISUAL_STYLE.md §1: white bold gothic w/ dark stroke,
 * vertical writing for title/end cards, small bracketed labels top-left.
 */

const strokeShadow =
  '0 0 3px rgba(0,0,0,.9), 0 0 6px rgba(0,0,0,.9), 1px 1px 0 rgba(0,0,0,.9), -1px -1px 0 rgba(0,0,0,.9)'

/**
 * P10-1 (2026-09-06): CSS font-family switch mirroring server/src/telop's
 * `familyFor` for the HTML/CSS approximation (PNG stays the source of
 * truth — this is only ever shown while the PNG is loading/501/erroring).
 * `undefined` for gothic/undefined -> the element's existing (pre-P10,
 * unset) font-family stack, unchanged.
 */
function fontFamilyForFallback(font: TelopFont | undefined): string | undefined {
  if (font === 'mincho') return '"Noto Serif JP","Hiragino Mincho ProN","Yu Mincho",serif'
  if (font === 'hand') return '"Yomogi","Hiragino Maru Gothic ProN",cursive'
  return undefined
}

export function TitleCardFallback({ overlay }: { overlay: TitleCardOverlay }) {
  const chars = [...overlay.text]
  const alignRight = overlay.position === 'right'
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
      style={{
        alignItems: alignRight ? 'flex-end' : 'center',
        paddingRight: alignRight ? '8%' : 0,
        fontFamily: fontFamilyForFallback(overlay.font),
      }}
    >
      {overlay.seasonBadge && (
        <div
          className="text-white/90 font-bold tracking-wide mb-2 px-2 py-0.5 border border-white/50 rounded-sm"
          style={{ fontSize: '2.6cqw', textShadow: strokeShadow }}
        >
          {overlay.seasonBadge}
        </div>
      )}
      <div
        className="flex text-white font-black"
        style={{ writingMode: overlay.vertical ? 'vertical-rl' : 'horizontal-tb', fontSize: '7.5cqw' }}
      >
        {chars.map((ch, i) => {
          const emphasized = overlay.emphasis && ch === overlay.emphasis.char
          return (
            <span
              key={i}
              style={{
                color: emphasized ? overlay.emphasis!.color : 'white',
                fontSize: emphasized ? `${overlay.emphasis!.scale}em` : undefined,
                textShadow: strokeShadow,
                lineHeight: 1.15,
              }}
            >
              {ch}
            </span>
          )
        })}
      </div>
      {overlay.dateText && (
        <div className="text-white/90 mt-3 text-xs tracking-wide" style={{ textShadow: strokeShadow }}>
          {overlay.dateText}
        </div>
      )}
      <div className="text-white/70 mt-1 text-[10px] tracking-[0.2em] uppercase" style={{ textShadow: strokeShadow }}>
        {venueLabel(overlay.venue)}
      </div>
    </div>
  )
}

export function LabelFallback({ overlay }: { overlay: LabelOverlay }) {
  // reviewer 差し戻し m2: server/src/telop/kinds/label.ts の anchorFor と同じ
  // 数値（top 基準 0.16 / 0.7 / 0.8）に揃える — 旧実装は bottom 基準の別値
  // だったため、previewMath.ts のスロットゴースト枠（同じ anchor を写した
  // もの）と実際のフォールバック描画がズレて見えていた（mock 時に顕著）。
  const posCls =
    overlay.position === 'left-upper'
      ? 'top-[16%] left-[6%] items-start'
      : overlay.position === 'left-lower'
        ? 'top-[70%] left-[6%] items-start'
        : 'top-[80%] inset-x-0 items-center'
  const boxed = overlay.style === 'bracket' || overlay.style === 'breedNagoya' || overlay.style === 'breedGinza'
  return (
    <div
      className={`absolute flex flex-col pointer-events-none ${posCls}`}
      style={{ fontFamily: fontFamilyForFallback(overlay.font) }}
    >
      <div
        className={`text-white font-bold text-sm ${boxed ? 'bg-black/45 px-2.5 py-1 rounded-sm' : ''}`}
        style={{ textShadow: strokeShadow }}
      >
        {overlay.style === 'bracket' ? `【${overlay.ja}】` : overlay.style === 'quote' ? `「${overlay.ja}」` : overlay.ja}
      </div>
      {overlay.en && <div className="text-white/80 text-[10px] mt-0.5" style={{ textShadow: strokeShadow }}>{overlay.en}</div>}
    </div>
  )
}

export function EndCardFallback({ overlay }: { overlay: EndCardOverlay }) {
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none bg-black/25"
      style={{ fontFamily: fontFamilyForFallback(overlay.font) }}
    >
      <div
        className="flex gap-3 text-white font-black"
        style={{ writingMode: overlay.vertical ? 'vertical-rl' : 'horizontal-tb', fontSize: '4.5cqw' }}
      >
        {overlay.lines.map((line, i) => (
          <div key={i} style={{ textShadow: strokeShadow }}>
            {line}
          </div>
        ))}
      </div>
      <div className="text-[10px] tracking-[0.2em] text-white/70 uppercase" style={{ textShadow: strokeShadow }}>
        {venueLabel(overlay.venue)}
      </div>
      {overlay.cta && (
        <div className="text-xs text-accent-gold font-semibold mt-1" style={{ textShadow: strokeShadow }}>
          {overlay.cta}
        </div>
      )}
    </div>
  )
}

export function LogoFallback({ overlay }: { overlay: LogoOverlay }) {
  return (
    <div
      className="absolute top-[3%] inset-x-0 flex justify-center pointer-events-none"
      style={{ opacity: overlay.opacity }}
    >
      <div className="w-10 h-10 rounded-full border-2 border-white/80 flex items-center justify-center text-white text-[8px] font-bold text-center leading-tight">
        AAM
      </div>
    </div>
  )
}

export function FreeTextFallback({ overlay }: { overlay: FreeTextOverlay }) {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${overlay.x * 100}%`,
        top: `${overlay.y * 100}%`,
        transform: 'translate(-50%, -50%)',
        writingMode: overlay.vertical ? 'vertical-rl' : 'horizontal-tb',
        color: overlay.color,
        fontSize: `${(overlay.sizePx / 1920) * 100}cqh`,
        fontFamily: fontFamilyForFallback(overlay.font),
        fontWeight: overlay.font === 'hand' ? 500 : 800,
        textShadow: overlay.strokeColor
          ? `0 0 4px ${overlay.strokeColor}, 1px 1px 0 ${overlay.strokeColor}, -1px -1px 0 ${overlay.strokeColor}`
          : strokeShadow,
      }}
    >
      {overlay.text}
    </div>
  )
}

function venueLabel(venue: string): string {
  switch (venue) {
    case 'ginza':
      return 'ART AQUARIUM MUSEUM GINZA'
    case 'nagoya':
      return 'EXHIBITION in NAGOYA'
    case 'saitama':
      return 'EXHIBITION in SAITAMA SUPER ARENA'
    default:
      return 'ART AQUARIUM'
  }
}
